import * as R from "remeda";
import { safeParseAsync, type z } from "zod";
import { createValidator } from "./validation/createValidator";
import type { FieldErrors, Validator } from "./validation/types";

const stringToPathArray = <T extends string>(path: T): (string | number)[] => {
	if (path.length === 0) return [];

	const match =
		path.match(/^\[(.+?)\](.*)$/) || path.match(/^\.?([^.[\]]+)(.*)$/);
	if (match) {
		const [_, key, rest] = match;
		return [/^\d+$/.test(key) ? Number(key) : key, ...stringToPathArray(rest)];
	}
	return [path];
};

const getIssuesForError = (err: z.ZodError<any>): z.core.$ZodIssue[] => {
	return err.issues.flatMap((issue) => {
		if (issue.code === "invalid_union") {
			return issue.errors.flat();
		} else {
			return [issue];
		}
	});
};

function pathToString(array: z.core.$ZodIssue["path"]) {
	let result = "";
	for (const item of array) {
		const prefix = result === "" ? "" : ".";
		result += isNaN(Number(item))
			? prefix + item.toString()
			: "[" + item.toString() + "]";
	}
	return result;
}

/**
 * Create a validator using a `zod` schema.
 */
export function withZod<DataType>(
	zodSchema: z.core.$ZodType<DataType>,
	parseParams?: z.core.ParseContext<z.core.$ZodIssue>,
): Validator<DataType> {
	return createValidator({
		validate: async (value) => {
			const result = await safeParseAsync(zodSchema, value, parseParams);
			if (result.success) return { data: result.data, error: undefined };

			const fieldErrors: FieldErrors = {};
			getIssuesForError(result.error).forEach((issue) => {
				const path = pathToString(issue.path);
				if (!fieldErrors[path]) fieldErrors[path] = issue.message;
			});
			return { error: fieldErrors, data: undefined };
		},
		validateField: async (data, field) => {
			const result = await safeParseAsync(zodSchema, data, parseParams);
			if (result.success) return { error: undefined };
			return {
				error: getIssuesForError(result.error).find((issue) =>
					R.equals(issue.path, stringToPathArray(field)),
				)?.message,
			};
		},
	});
}
